import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useNotify, useRefresh } from 'react-admin';
import { getToken, getRefreshToken } from '../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

/** A field whose case data changed since the form was confirmed (FieldDrift, nestjs). */
interface FieldDrift {
  field: string;
  label: string;
  was: string | boolean;
  now: string | boolean;
  edited: boolean;
}

const show = (v: string | boolean) => (v === true ? 'checked' : v === false ? 'unchecked' : v || '(blank)');

const authHeaders = async () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${await getToken()}`,
  'Refresh-Token': await getRefreshToken(),
});

/**
 * Reset (unlock) for a confirmed benefit form. A confirmed form keeps exactly
 * what was submitted, so later case changes don't reach it; Reset moves that
 * submission to the form's history and regenerates the form from the case.
 *
 * Returns the toolbar button, the "case data changed" banner and the confirm
 * dialog, for the review modal to place. `enabled` is false while the modal is
 * closed or the form is locked for another reason (signed).
 */
export function useConfirmedFormReset({
  funeralId,
  formKey,
  title,
  confirmed,
  enabled,
  onReset,
}: {
  funeralId: number;
  formKey: string | undefined;
  title: string | undefined;
  confirmed: boolean;
  enabled: boolean;
  /** After a successful reset: reload the PDF and the parent's status. */
  onReset: () => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [drift, setDrift] = useState<FieldDrift[]>([]);
  const [askReset, setAskReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const active = enabled && confirmed && !!formKey;

  useEffect(() => {
    setDrift([]);
    if (!active) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${PAPERMAN_API_URL}form-confirmations/${funeralId}/drift/${formKey}`, {
          headers: await authHeaders(),
        });
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled) setDrift(Array.isArray(body?.changes) ? body.changes : []);
      } catch {
        // The banner is a hint; without it the modal works as before.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, funeralId, formKey]);

  const handleReset = useCallback(async () => {
    if (!formKey) return;
    setResetting(true);
    try {
      const res = await fetch(`${PAPERMAN_API_URL}form-confirmations/${funeralId}/reset`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ formKey }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.message || `Reset failed (${res.status})`);
      }
      setAskReset(false);
      setDrift([]);
      notify(`${title} reset to current case data — review and save it again`, { type: 'success' });
      onReset();
      refresh();
    } catch (err: any) {
      notify(err?.message || 'Failed to reset form', { type: 'error' });
    } finally {
      setResetting(false);
    }
  }, [formKey, funeralId, title, notify, onReset, refresh]);

  const resetButton = active ? (
    <Button
      onClick={() => setAskReset(true)}
      variant="text"
      color="inherit"
      size="small"
      disabled={resetting}
      startIcon={<RestartAltIcon />}
    >
      Reset to current case data
    </Button>
  ) : null;

  const driftBanner =
    active && drift.length > 0 ? (
      <Alert
        severity="warning"
        sx={{ borderRadius: 0 }}
        action={
          <Button color="inherit" size="small" onClick={() => setAskReset(true)} disabled={resetting}>
            Reset
          </Button>
        }
      >
        Case data changed since this was confirmed:{' '}
        {drift.map((d, i) => (
          <React.Fragment key={d.field}>
            {i > 0 && '; '}
            <strong>{d.label}</strong> was {show(d.was)}, now {show(d.now)}
            {d.edited && ' (edited by staff — Reset discards the edit)'}
          </React.Fragment>
        ))}{' '}
        — Reset to update.
      </Alert>
    ) : null;

  const resetDialog = (
    <Dialog open={askReset} onClose={() => !resetting && setAskReset(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Reset {title}?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          This discards the confirmed values and regenerates the form from the case. The previous
          submission stays in history.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAskReset(false)} disabled={resetting}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleReset()}
          variant="contained"
          color="warning"
          disabled={resetting}
          startIcon={resetting ? <CircularProgress size={16} color="inherit" /> : <RestartAltIcon />}
        >
          Reset
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { resetButton, driftBanner, resetDialog };
}
